import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, openSignedFile } from "@/lib/api";
import { fmtEUR, fmtDate, L } from "@/constants/labels";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Star, MessageSquare, FileText, CheckCircle2, AlertTriangle, XCircle, CircleDashed, Clock, Lock } from "lucide-react";
import RatingStars from "@/components/RatingStars";
import ResponseModal from "@/components/ResponseModal";
import { AuthImage } from "@/components/AuthImage";
import { ProfileGallery } from "@/components/ProfileGallery";

// Chantier 2 (2026-09) — statuts canoniques (verifie, a_controler,
// expire_bientot, expire, non_fourni, refuse) + alias legacy pour ne pas
// casser les documents pré-migration éventuellement en base.
const STATUT_STYLE = {
  verifie: { icon: CheckCircle2, cls: "text-[#10B981]" },
  a_controler: { icon: Clock, cls: "text-[#E8710A]" },
  expire_bientot: { icon: AlertTriangle, cls: "text-[#E8710A]" },
  expire: { icon: XCircle, cls: "text-[#C0392B]" },
  refuse: { icon: XCircle, cls: "text-[#C0392B]" },
  non_fourni: { icon: CircleDashed, cls: "text-[#94A3B8]" },
  // Alias legacy — supportés le temps que le back complète la migration.
  valide: { icon: CheckCircle2, cls: "text-[#10B981]" },
  expirant: { icon: AlertTriangle, cls: "text-[#E8710A]" },
  a_verifier: { icon: Clock, cls: "text-[#E8710A]" },
};

// Cellule "Voir" du tableau des fichiers déposés — remplace le vide silencieux
// par une explication de la raison du blocage (Chantier 1, 2026-02).
function DocOpenCell({ doc }) {
  if (doc.signed_url) {
    const onClick = async (e) => {
      e.preventDefault();
      try {
        await openSignedFile(doc.signed_url, {
          onQuota: (q) => toast.error(q.title, { description: `${q.message} ${q.detail}`, duration: 8000 }),
        });
      } catch (err) {
        toast.error("Ouverture impossible. Réessayez.");
      }
    };
    return (
      <button
        type="button"
        onClick={onClick}
        className="btn-outline"
        title={L.wording.prestaProfileConsultationInformera}
        data-testid={`doc-open-${doc.type}`}
      >
        <FileText size={14} /> Consulter
      </button>
    );
  }
  // signed_url null → afficher la raison au lieu de laisser vide
  const reason = doc.locked_reason;
  if (reason === "restrict_to_engagement") {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-[#E8710A]"
        title={L.wording.prestaProfileConsultationRestreinte}
        data-testid={`doc-locked-${doc.type}`}
      >
        <Lock size={12} /> Réservé à l'engagement
      </span>
    );
  }
  // Ne devrait pas arriver si docs_of_user renvoie la ligne — sécurité
  return <span className="text-xs text-[#94A3B8]">—</span>;
}

function AdminDossierPanel({ summary, detail }) {
  // Résumé haut : 4/5 conformes · 1 expirant sous 30j · 0 expiré · 0 manquant
  const s = summary || {};
  const stateLabel = ({
    verifie: "Dossier complet et à jour",
    valide: "Dossier complet et à jour",
    expire_bientot: "Dossier complet — au moins un document arrive à expiration",
    expirant: "Dossier complet — au moins un document arrive à expiration",
    expire: "Dossier avec au moins un document expiré",
    a_controler: "Dossier en cours de vérification",
    non_fourni: "Dossier incomplet",
    incomplet: "Dossier incomplet",
  })[s.status] || "État inconnu";
  const stateCls = ({
    verifie: "text-[#10B981]",
    valide: "text-[#10B981]",
    expire_bientot: "text-[#E8710A]",
    expirant: "text-[#E8710A]",
    expire: "text-[#C0392B]",
    a_controler: "text-[#E8710A]",
    non_fourni: "text-[#94A3B8]",
    incomplet: "text-[#94A3B8]",
  })[s.status] || "text-[#3E4C59]";
  return (
    <div className="mt-6 card p-4" data-testid="dossier-admin-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-semibold text-[#1F2933]">Dossier administratif</h2>
        <div className={`text-sm font-semibold ${stateCls}`}>{stateLabel}</div>
      </div>
      <p className="text-xs text-[#3E4C59] mt-1 leading-relaxed">
        Les documents administratifs de ce prestataire sont consultables. Chaque consultation
        est enregistrée et communiquée au prestataire.
      </p>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <div className="rounded border border-[#E4E7EB] p-2">
          <div className="text-[#3E4C59]">Documents fournis</div>
          <div className="text-lg font-bold text-[#1B3A5C]">{s.docs_fournis}/{s.total}</div>
        </div>
        <div className="rounded border border-[#E4E7EB] p-2">
          <div className="text-[#3E4C59]">{L.compliance.verifie}</div>
          <div className="text-lg font-bold text-[#10B981]">{s.docs_valides}</div>
        </div>
        <div className="rounded border border-[#E4E7EB] p-2">
          <div className="text-[#3E4C59]">Expirent {"<"} 30 j</div>
          <div className="text-lg font-bold text-[#E8710A]">{s.docs_expirant_30j}</div>
        </div>
        <div className="rounded border border-[#E4E7EB] p-2">
          <div className="text-[#3E4C59]">Expirés</div>
          <div className="text-lg font-bold text-[#C0392B]">{s.docs_expires}</div>
        </div>
      </div>
      <table className="table-compact w-full mt-3">
        <thead>
          <tr><th>Document</th><th>Statut</th><th>Expiration</th><th>Reste</th></tr>
        </thead>
        <tbody>
          {(detail || []).map((d) => {
            const cfg = STATUT_STYLE[d.statut] || STATUT_STYLE.non_fourni;
            const Icon = cfg.icon;
            const dl = d.days_left;
            const restLabel = d.date_expiration
              ? (dl < 0 ? `expiré depuis ${-dl} j`
                : dl < 30 ? `${dl} j restants` : `${dl} j`)
              : "—";
            const restCls =
              d.statut === "non_fourni" ? "text-[#94A3B8]"
              : dl == null ? "text-[#3E4C59]"
              : dl < 0 ? "text-[#C0392B]"
              : dl < 30 ? "text-[#E8710A]" : "text-[#3E4C59]";
            return (
              <tr key={d.type} data-testid={`dossier-row-${d.type}`}>
                <td>{L.docTypes[d.type] || d.type}</td>
                <td>
                  <span className={`inline-flex items-center gap-1 ${cfg.cls}`}>
                    <Icon size={14} /> {L.compliance[d.statut] || d.statut}
                  </span>
                </td>
                <td>{d.date_expiration ? fmtDate(d.date_expiration) : "—"}</td>
                <td className={restCls}>{restLabel}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function PrestataireProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [docs, setDocs] = useState([]);
  const [responseAvis, setResponseAvis] = useState(null);

  const canViewDocs = user?.role === "entreprise" || user?.role === "admin";
  const load = useCallback(async () => {
    const { data: profile } = await api.get(`/prestataires/${id}`);
    setData(profile);
    if (canViewDocs) {
      const r = await api.get(`/documents/user/${id}`).catch(() => ({ data: [] }));
      setDocs(r.data);
    }
  }, [id, canViewDocs]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-sm text-[#3E4C59]">{L.common.loading}</div>;
  const p = data.profile || {};

  const openThread = () => {
    // reuse besoin_id-less thread with a stable id
    const threadId = [user.id, id].sort().join(":");
    window.location.assign(`/app/messages?open=${threadId}&to=${id}`);
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-4">
          {data.logo_url && (
            <AuthImage url={data.logo_url} alt={`Logo ${data.raison_sociale}`}
              className="w-16 h-16 md:w-20 md:h-20 object-cover rounded-[4px] border border-[#E4E7EB] bg-white shrink-0"
              testId="profile-logo" />
          )}
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-[#1F2933]">{data.raison_sociale}</h1>
            <p className="text-sm text-[#3E4C59]">SIRET {data.siret}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {user?.role === "entreprise" && (
            <button onClick={openThread} className="btn-primary" data-testid="contact-btn"><MessageSquare size={16} /> Contacter</button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mt-6">
        <div className="card p-4 md:col-span-2">
          <h2 className="text-lg font-semibold text-[#1F2933]">Prestation</h2>
          <dl className="mt-2 text-sm grid grid-cols-2 gap-y-2">
            <dt className="text-[#3E4C59]">Métier</dt><dd>{p.metier || "—"}</dd>
            <dt className="text-[#3E4C59]">Spécialité</dt><dd>{p.specialite || "—"}</dd>
            <dt className="text-[#3E4C59]">Sous-spécialité</dt><dd>{p.sous_specialite || "—"}</dd>
            <dt className="text-[#3E4C59]">Ville</dt><dd>{p.ville || "—"} {p.code_postal ? `(${p.code_postal})` : ""}</dd>
            <dt className="text-[#3E4C59]">Rayon</dt><dd>{p.rayon_km ? `${p.rayon_km} km` : "—"}</dd>
            <dt className="text-[#3E4C59]">Tarif horaire</dt><dd>{fmtEUR(p.tarif_horaire)}</dd>
            <dt className="text-[#3E4C59]">Tarif journalier</dt><dd>{fmtEUR(p.tarif_journalier)}</dd>
            {p.tarif_mensuel_ht ? (
              <>
                <dt className="text-[#3E4C59]">Tarif mensuel (location)</dt>
                <dd>{fmtEUR(p.tarif_mensuel_ht)}</dd>
              </>
            ) : null}
            <dt className="text-[#3E4C59]">Certifications</dt><dd>{(p.certifications || []).join(", ") || "—"}</dd>
          </dl>
          {p.description && <p className="mt-4 text-sm text-[#1F2933] leading-relaxed">{p.description}</p>}
        </div>
        <div className="card p-4">
          <h2 className="text-lg font-semibold text-[#1F2933]">Notations</h2>
          <div className="mt-3 space-y-3" data-testid="reputation-blocks">
            <div>
              <div className="text-xs uppercase text-[#3E4C59]">{L.wording.prestaProfileEnTantQue}</div>
              <div className="text-2xl font-bold text-[#1B3A5C] inline-flex items-center gap-2">
                <Star size={20} className="text-[#E8710A]" />
                {data.note_prestataire ?? data.note_moyenne ?? "—"}
              </div>
              <div className="text-xs text-[#3E4C59]">
                {data.nb_avis_prestataire ?? data.nb_avis ?? 0} avis
              </div>
            </div>
            {(data.roles || []).includes("entreprise") && (
              <div className="pt-3 border-t border-[#E4E7EB]">
                <div className="text-xs uppercase text-[#3E4C59]">En tant que donneur d'ordre</div>
                <div className="text-2xl font-bold text-[#1B3A5C] inline-flex items-center gap-2">
                  <Star size={20} className="text-[#E8710A]" />
                  {data.note_entreprise ?? "—"}
                </div>
                <div className="text-xs text-[#3E4C59]">
                  {data.nb_avis_entreprise ?? 0} avis
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <ProfileGallery photos={data.photos} canReport={user?.role === "entreprise"} isOwner={user?.id === id || user?.role === "admin"} />

      {(p.portfolio_urls || []).length > 0 && (
        <div className="mt-6">
          <h2 className="text-lg font-semibold text-[#1F2933]">Liens externes</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
            {p.portfolio_urls.map((u, i) => (
              <img key={i} src={u} alt="Chantier" className="w-full h-40 object-cover border border-[#E4E7EB] rounded-[4px]" />
            ))}
          </div>
        </div>
      )}

      {/* Panneau conformité PUBLIC — visible par toute personne consultant le
          profil. N'expose PAS les fichiers eux-mêmes, seulement statuts et
          dates d'expiration. Le tableau plus bas (avec liens de téléchargement)
          reste gate-guardé pour entreprise/admin. */}
      <AdminDossierPanel summary={data.conformite_summary} detail={data.conformite_detail} />

      {(user?.role === "entreprise" || user?.role === "admin") && (
        <div className="mt-6 card overflow-x-auto">
          <div className="p-3 font-semibold text-[#1F2933] border-b border-[#E4E7EB]">Fichiers déposés</div>
          <table className="table-compact w-full">
            <thead><tr><th>Type</th><th>Dépôt</th><th>Expiration</th><th>Statut</th><th></th></tr></thead>
            <tbody>
              {docs.length === 0 && <tr><td colSpan={5} className="text-center text-[#3E4C59]">Aucun document déposé</td></tr>}
              {docs.map(d => (
                <tr key={d.id}>
                  <td>{L.docTypes[d.type] || d.type}</td>
                  <td>{fmtDate(d.date_depot)}</td>
                  <td>{fmtDate(d.date_expiration)}</td>
                  <td>{L.compliance[d.statut] || d.statut}</td>
                  <td><DocOpenCell doc={d} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="p-3 text-xs text-[#3E4C59] border-t border-[#E4E7EB]">
            Chaque téléchargement est enregistré dans le journal du prestataire (raison sociale, SIRET, horodatage).
            Le fichier PDF servi porte un filigrane à votre en-tête. Plafond : 20 dossiers différents / 24 h.
          </p>
        </div>
      )}

      <div className="mt-6 card p-4">
        <h2 className="text-lg font-semibold text-[#1F2933]">Avis reçus</h2>
        {(data.avis || []).length === 0 && <p className="text-sm text-[#3E4C59] mt-2">Aucun avis pour le moment.</p>}
        {(data.avis || []).map(a => {
          const canReply = user?.id === a.to_user_id;
          return (
            <div key={a.id} className="border-t border-[#E4E7EB] py-3">
              <div className="flex items-center gap-2 flex-wrap">
                <RatingStars value={a.note} readOnly size={16} />
                <span className="text-sm font-semibold text-[#E8710A]">{a.note}/5</span>
                <span className="text-xs text-[#3E4C59]">— {fmtDate(a.created_at)}</span>
                {canReply && !a.reponse && (
                  <button
                    onClick={() => setResponseAvis(a)}
                    className="btn-outline text-xs flex items-center gap-1 ml-auto"
                    data-testid={`profile-reply-${a.id}`}
                  ><MessageSquare size={12} /> Répondre</button>
                )}
                {canReply && a.reponse && (
                  <button
                    onClick={() => setResponseAvis(a)}
                    className="btn-ghost text-xs flex items-center gap-1 ml-auto text-[#3E4C59]"
                    data-testid={`profile-edit-reply-${a.id}`}
                  ><MessageSquare size={12} /> Modifier ma réponse</button>
                )}
              </div>
              <p className="mt-1 text-sm text-[#1F2933] whitespace-pre-line">{a.commentaire}</p>
              {a.reponse && (
                <div className="mt-2 text-sm text-[#3E4C59] pl-3 border-l-2 border-[#1B3A5C] whitespace-pre-line">
                  <b>Réponse du destinataire :</b> {a.reponse}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6"><Link to="/app/prestataires" className="btn-ghost">← Retour</Link></div>

      {responseAvis && (
        <ResponseModal
          avis={responseAvis}
          onClose={() => setResponseAvis(null)}
          onSubmitted={load}
        />
      )}
    </div>
  );
}
